import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { CalendarClock, Columns3, Download, List, Plus, Table2 } from 'lucide-react';
import type { TaskSort, ViewKind } from '@shared/types';
import { KanbanBoard } from '@/components/KanbanBoard';
import { TaskRow } from '@/components/TaskRow';
import {
  Button,
  CenteredSpinner,
  EmptyState,
  ErrorState,
  SkeletonRows,
} from '@/components/ui';
import { cn } from '@/lib/format';
import {
  useBuckets,
  useCreateTask,
  useProject,
  useTasks,
  useUpdateTask,
} from '@/lib/queries';
import { TaskTable } from '@/components/TaskTable';
import { GanttView } from '@/components/GanttView';

const VIEW_ICONS: Partial<Record<ViewKind, React.ReactNode>> = {
  list: <List className="size-3.5" />,
  kanban: <Columns3 className="size-3.5" />,
  table: <Table2 className="size-3.5" />,
  gantt: <CalendarClock className="size-3.5" />,
};

export function ProjectPage() {
  const { projectId } = useParams({ from: '/shell/projects/$projectId' });
  const { data, isPending, error, refetch } = useProject(projectId);

  const [kind, setKind] = useState<ViewKind>('list');
  const [showDone, setShowDone] = useState(false);
  const [sort, setSort] = useState<TaskSort>('due_date');
  const [includeSub, setIncludeSub] = useState(false);

  if (isPending) return <CenteredSpinner label="Projekt wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { project, views } = data;
  const available = views;
  const kanbanView = views.find((v) => v.kind === 'kanban');

  return (
    <div className="flex h-full flex-col">
      <header className="border-b line surface-card px-5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto min-w-0">
            <h1 className="truncate text-base font-semibold text-strong">{project.title}</h1>
            {project.description && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted">
                {stripHtml(project.description)}
              </p>
            )}
          </div>

          <a
            href={`/api/export.xlsx?projectId=${projectId}`}
            className="flex items-center gap-1.5 rounded-lg border line surface-card px-2.5 py-1
                       text-xs font-medium text-muted hover:text-strong hover:surface-hover"
            title="Als Excel exportieren"
          >
            <Download className="size-3.5" />
            Export
          </a>

          <div className="flex rounded-lg surface-sunken p-0.5">
            {available.map((view) => (
              <button
                key={view.id}
                onClick={() => setKind(view.kind)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  kind === view.kind
                    ? 'surface-card text-strong shadow-sm'
                    : 'text-muted hover:text-strong',
                )}
              >
                {VIEW_ICONS[view.kind]}
                {view.title}
              </button>
            ))}
          </div>
        </div>

        {/* Kanban and Gantt have their own layout/ordering, so the list
            sort/filter controls would be misleading there. */}
        {kind !== 'kanban' && kind !== 'gantt' && (
          <div className="mt-2.5 flex flex-wrap items-center gap-3 text-xs">
            <label className="flex cursor-pointer items-center gap-1.5 text-muted">
              <input
                type="checkbox"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
                className="size-3.5 rounded accent-brand-500"
              />
              Erledigte anzeigen
            </label>

            <label className="flex cursor-pointer items-center gap-1.5 text-muted">
              <input
                type="checkbox"
                checked={includeSub}
                onChange={(e) => setIncludeSub(e.target.checked)}
                className="size-3.5 rounded accent-brand-500"
              />
              Unterprojekte einbeziehen
            </label>

            <label className="ml-auto flex items-center gap-1.5 text-muted">
              Sortieren
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as TaskSort)}
                className="rounded border line surface-card px-1.5 py-1 text-xs text-strong"
              >
                <option value="due_date">Fälligkeit</option>
                <option value="position">Manuell</option>
                <option value="title">Titel</option>
                <option value="priority">Priorität</option>
                <option value="updated">Zuletzt geändert</option>
                <option value="created">Erstellt</option>
              </select>
            </label>
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1">
        {kind === 'kanban' && kanbanView ? (
          <KanbanView projectId={projectId} viewId={kanbanView.id} />
        ) : kind === 'gantt' ? (
          <GanttTimeline projectId={projectId} />
        ) : (
          <TaskListView
            projectId={projectId}
            projectTitle={project.title}
            kind={kind}
            showDone={showDone}
            sort={sort}
            includeSub={includeSub}
          />
        )}
      </div>
    </div>
  );
}

function GanttTimeline({ projectId }: { projectId: number }) {
  // Include done tasks so the timeline shows completed work in context;
  // fetch a generous page since Gantt shows everything at once.
  const { data, isPending, error, refetch } = useTasks({ projectId, perPage: 500, sort: 'due_date' });
  if (isPending) return <CenteredSpinner label="Zeitleiste wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <GanttView tasks={data?.items ?? []} />;
}

function KanbanView({ projectId, viewId }: { projectId: number; viewId: number }) {
  const { data, isPending, error, refetch } = useBuckets(projectId, viewId);
  if (isPending) return <CenteredSpinner label="Board wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data || (data.buckets.length === 0 && data.unassigned.length === 0)) {
    return (
      <EmptyState
        icon={<Columns3 />}
        title="Keine Spalten"
        description="Dieses Board hat noch keine Spalten."
      />
    );
  }
  return <KanbanBoard viewId={viewId} buckets={data.buckets} unassigned={data.unassigned} />;
}

function TaskListView({
  projectId,
  projectTitle,
  kind,
  showDone,
  sort,
  includeSub,
}: {
  projectId: number;
  projectTitle: string;
  kind: ViewKind;
  showDone: boolean;
  sort: TaskSort;
  includeSub: boolean;
}) {
  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const [newTitle, setNewTitle] = useState('');

  const query = useTasks({
    projectId,
    includeSubprojects: includeSub,
    done: showDone ? undefined : false,
    sort,
    order: sort === 'priority' ? 'desc' : 'asc',
    perPage: 500,
  });

  const tasks = useMemo(() => query.data?.items ?? [], [query.data]);

  function submitNew(e: React.FormEvent) {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    createTask.mutate({ projectId, title });
    setNewTitle('');
  }

  return (
    <div className="flex h-full flex-col">
      <div className="scroll-thin min-h-0 flex-1 overflow-auto">
        {query.isPending ? (
          <SkeletonRows />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={<List />}
            title="Keine Aufgaben"
            description={
              showDone
                ? `${projectTitle} enthält keine Aufgaben.`
                : 'Alles erledigt. Erledigte Aufgaben einblenden, um sie zu sehen.'
            }
          />
        ) : kind === 'table' ? (
          <TaskTable
            tasks={tasks}
            onToggleDone={(t) => updateTask.mutate({ id: t.id, patch: { done: !t.done } })}
          />
        ) : (
          <div className="divide-y line surface-card">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onToggleDone={(t) => updateTask.mutate({ id: t.id, patch: { done: !t.done } })}
              />
            ))}
          </div>
        )}
      </div>

      <form onSubmit={submitNew} className="flex gap-2 border-t line surface-card p-3">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Neue Aufgabe hinzufügen…"
          className="min-w-0 flex-1 rounded-lg border line surface-card px-3 py-1.5 text-sm
                     text-strong placeholder:text-faint focus:border-brand-500
                     focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={createTask.isPending}
          disabled={!newTitle.trim()}
        >
          <Plus className="size-3.5" />
          Hinzufügen
        </Button>
      </form>
    </div>
  );
}

/** Project descriptions are HTML; the header only has room for a text line. */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
